import { ResponsiveBar } from '@nivo/bar'

import { useQuery } from '@redwoodjs/web'

import { VoiceAgentAnalysisResultName } from 'src/graphql/types/voiceAgentResult'
import { GET_VOICE_AGENT_ANALYSIS_TOTALS } from 'src/graphql/voiceAgentAnalysisResult.graphql'
import { formatSnakeValue } from 'src/lib/formatters'
import { voiceAgentAnalysisColors } from 'src/pages/CallAnalyticsPage/utils'

type BarChartData = {
  analysisResult: VoiceAgentAnalysisResultName
  [key: string]: VoiceAgentAnalysisResultName | number
}

const VoiceAgentBarChart = ({ filters, totalConnectedCalls }) => {
  const { data: totalsData, loading: totalsLoading } = useQuery(
    GET_VOICE_AGENT_ANALYSIS_TOTALS,
    {
      variables: {
        filters,
      },
    }
  )
  const analysisTotals = totalsData?.voiceAgentAnalysisTotals || []

  const barChartData: BarChartData[] = Object.values(
    VoiceAgentAnalysisResultName
  )
    .map((key) => ({
      analysisResult: key,
      [key]: analysisTotals.find((result) => result.name === key)?.count || 0,
    }))
    .sort((a, b) => {
      const aValue = a[a.analysisResult] as number
      const bValue = b[b.analysisResult] as number
      return bValue - aValue
    })

  return (
    <div className="flex h-[150px] flex-row items-center justify-between">
      {barChartData.every((result) => result[result.analysisResult] === 0) ? (
        <div className="flex w-full flex-col items-center justify-center text-sm font-medium text-doubleNickel-gray-500">
          No data available
        </div>
      ) : (
        <ResponsiveBar
          data={barChartData}
          keys={Object.values(VoiceAgentAnalysisResultName)}
          indexBy="analysisResult"
          margin={{ bottom: 30 }}
          padding={0.1}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={({ id }) =>
            voiceAgentAnalysisColors[id as VoiceAgentAnalysisResultName]
          }
          borderRadius={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 1.4]],
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) => formatSnakeValue(value),
            truncateTickAt: 0,
          }}
          axisLeft={null}
          enableTotals={false}
          enableGridX={false}
          enableGridY={false}
          totalsOffset={9}
          labelSkipWidth={9}
          labelSkipHeight={16}
          labelTextColor={{
            from: 'color',
            modifiers: [['darker', 1.6]],
          }}
          labelOffset={1}
          enableLabel={true}
          label={(d) => String(d.value || '0')}
          tooltip={({ id, value }) => (
            <div className="rounded bg-white p-2 text-sm shadow">
              <strong>{formatSnakeValue(id as string)}</strong>: {value}
              <br />
              <span className="text-xs text-doubleNickel-gray-500">
                {((value / totalConnectedCalls) * 100).toFixed(0)}% of connected
                calls
              </span>
            </div>
          )}
        />
      )}
    </div>
  )
}

export default VoiceAgentBarChart
